<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import ContactAPI from 'dashboard/api/contacts';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  title: { type: String, default: '' },
  confirmLabel: { type: String, default: '' },
  // Multiple: checkboxes + confirm button. Single: clicking a contact picks it.
  multiple: { type: Boolean, default: false },
});

const emit = defineEmits(['confirm']);

const { t } = useI18n();

const dialogRef = ref(null);
const contacts = ref([]);
const totalCount = ref(0);
const page = ref(1);
const query = ref('');
const isFetching = ref(false);
const selected = ref(new Map());

const hasMore = computed(() => contacts.value.length < totalCount.value);
const confirmButtonLabel = computed(
  () =>
    props.confirmLabel ||
    t('CONTACT_PICKER.CONFIRM', { n: selected.value.size })
);

const fetchContacts = async (nextPage = 1) => {
  isFetching.value = true;
  try {
    const search = query.value.trim();
    const { data } = search
      ? await ContactAPI.search(encodeURIComponent(search), nextPage)
      : await ContactAPI.get(nextPage);
    contacts.value =
      nextPage === 1 ? data.payload : [...contacts.value, ...data.payload];
    totalCount.value = data.meta.count;
    page.value = nextPage;
  } finally {
    isFetching.value = false;
  }
};

const onSearch = debounce(value => {
  query.value = value;
  fetchContacts();
}, 300);

const toggle = contact => {
  const next = new Map(selected.value);
  if (next.has(contact.id)) next.delete(contact.id);
  else next.set(contact.id, contact);
  selected.value = next;
};

const onContactClick = contact => {
  if (props.multiple) {
    toggle(contact);
    return;
  }
  emit('confirm', [contact]);
  dialogRef.value?.close();
};

const onConfirm = () => {
  if (!selected.value.size) return;
  emit('confirm', [...selected.value.values()]);
  dialogRef.value?.close();
};

const open = () => {
  query.value = '';
  selected.value = new Map();
  fetchContacts();
  dialogRef.value?.open();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="xl"
    :title="title || t('CONTACT_PICKER.TITLE')"
    :confirm-button-label="confirmButtonLabel"
    :show-confirm-button="multiple"
    :disable-confirm-button="!selected.size"
    @confirm="onConfirm"
  >
    <div class="flex flex-col gap-3">
      <Input
        :model-value="query"
        type="search"
        :placeholder="t('CONTACT_PICKER.SEARCH_PLACEHOLDER')"
        @input="onSearch($event.target.value)"
      />
      <div class="flex flex-col gap-1 overflow-y-auto max-h-96">
        <button
          v-for="contact in contacts"
          :key="contact.id"
          type="button"
          class="flex items-center gap-3 px-2 py-2 text-left rounded-lg hover:bg-n-alpha-2"
          @click="onContactClick(contact)"
        >
          <Checkbox
            v-if="multiple"
            :model-value="selected.has(contact.id)"
            class="pointer-events-none"
          />
          <Avatar :name="contact.name" :src="contact.thumbnail" :size="28" />
          <div class="flex flex-col min-w-0">
            <span class="text-sm font-medium truncate text-n-slate-12">
              {{ contact.name }}
            </span>
            <span class="text-xs truncate text-n-slate-11">
              {{ contact.phone_number || contact.email }}
            </span>
          </div>
        </button>
        <span
          v-if="!contacts.length && !isFetching"
          class="p-4 text-sm text-center text-n-slate-11"
        >
          {{ t('CONTACT_PICKER.EMPTY') }}
        </span>
        <div v-if="isFetching" class="flex justify-center p-2">
          <Spinner />
        </div>
        <Button
          v-else-if="hasMore"
          :label="t('CONTACT_PICKER.LOAD_MORE')"
          variant="ghost"
          color="slate"
          size="sm"
          type="button"
          class="self-center"
          @click="fetchContacts(page + 1)"
        />
      </div>
    </div>
  </Dialog>
</template>
